/**
 * Tour API functions
 * Public functions for managing tour lifecycle and UI
 */

import { TOUR_STYLES_ID, TOUR_CSS } from './styles'
import type { TourDemoType } from './types'
import { getDemoHTML } from './demos'
import { escapeHtml } from '../html/escape-html'
import { shellText as chrome } from '../i18n'

// Popover chrome (buttons, keyboard hint, close label): `shell:tour_popover.*`
// (packages/shell/src/locales). The bundled Vietnamese keeps the popover
// readable when no i18n instance is registered (an extracted module running
// standalone).

/**
 * Inject tour styles into the document
 * Safe to call multiple times - only injects once
 */
export const injectTourStyles = (): void => {
    if (document.getElementById(TOUR_STYLES_ID)) return

    const style = document.createElement('style')
    style.id = TOUR_STYLES_ID
    style.textContent = TOUR_CSS
    document.head.appendChild(style)
}

// Store keyboard handler reference for cleanup
let currentKeyboardHandler: ((e: KeyboardEvent) => void) | null = null

// --- Spotlight tracking -----------------------------------------------------
// The backdrop has a hole over the element carrying `.tour-highlight-element`
// (see styles.ts for why the target is not raised above the backdrop instead).
// One rAF loop, running only while the overlay exists, keeps the hole (and the
// popover) glued to the target through smooth scrolling, scroll containers,
// window resize, a docked side panel opening and async layout shifts: it only
// touches the DOM when the target's box actually changed.

/** Space between the target's box and the edge of the spotlight, in px. */
const SPOTLIGHT_PAD = 6

let trackerFrame = 0
let lastTargetKey = ''
let trackedPopover: { popover: HTMLElement; target: HTMLElement } | null = null

const stopTracking = (): void => {
    cancelAnimationFrame(trackerFrame)
    trackerFrame = 0
    lastTargetKey = ''
    trackedPopover = null
}

const trackTarget = (): void => {
    const overlay = document.querySelector<HTMLElement>('.tour-overlay')
    if (!overlay) {
        stopTracking()
        return
    }
    const spot = overlay.querySelector<HTMLElement>('.tour-spotlight')
    const target = document.querySelector<HTMLElement>(
        '.tour-highlight-element'
    )
    const rect = target?.getBoundingClientRect()
    if (spot && target && rect && rect.width > 0 && rect.height > 0) {
        const key = [
            rect.left,
            rect.top,
            rect.width,
            rect.height,
            window.innerWidth,
            window.innerHeight,
        ].join('|')
        if (key !== lastTargetKey) {
            lastTargetKey = key
            overlay.dataset.spotlight = 'on'
            spot.style.left = `${rect.left - SPOTLIGHT_PAD}px`
            spot.style.top = `${rect.top - SPOTLIGHT_PAD}px`
            spot.style.width = `${rect.width + SPOTLIGHT_PAD * 2}px`
            spot.style.height = `${rect.height + SPOTLIGHT_PAD * 2}px`
            if (
                trackedPopover &&
                trackedPopover.target === target &&
                trackedPopover.popover.isConnected
            ) {
                positionPopover(trackedPopover.popover, target)
            }
        }
    } else if (overlay.dataset.spotlight !== 'off') {
        // No (visible) target: dim the whole page, like an info-only step
        overlay.dataset.spotlight = 'off'
        lastTargetKey = ''
    }
    trackerFrame = requestAnimationFrame(trackTarget)
}

/**
 * Create tour overlay element
 */
export const createTourOverlay = (): void => {
    if (document.querySelector('.tour-overlay')) return

    const overlay = document.createElement('div')
    overlay.className = 'tour-overlay'
    overlay.dataset.spotlight = 'off'
    const spotlight = document.createElement('div')
    spotlight.className = 'tour-spotlight'
    overlay.appendChild(spotlight)
    document.body.appendChild(overlay)

    stopTracking()
    trackerFrame = requestAnimationFrame(trackTarget)
}

/**
 * Cleanup all tour elements from the DOM
 */
export const cleanupTourElements = (): void => {
    stopTracking()

    // Remove keyboard handler
    if (currentKeyboardHandler) {
        document.removeEventListener('keydown', currentKeyboardHandler)
        currentKeyboardHandler = null
    }

    document
        .querySelector('.tour-highlight-element')
        ?.classList.remove('tour-highlight-element')
    document.querySelector('.tour-popover-container')?.remove()
    document.querySelector('.tour-overlay')?.remove()
}

/**
 * Create tour popover element with content
 */
export const createTourPopover = (options: {
    title: string
    description: string
    currentStep: number
    totalSteps: number
    onPrev?: () => void
    onNext: () => void
    onClose: () => void
    isLastStep?: boolean
    demo?: TourDemoType
    /**
     * Waiting for a page / target (cross-page step): shows a spinner instead of
     * the description and disables Previous / Next. Close and Escape still work.
     */
    loading?: boolean
    /** An extra outline button before Next (the "Open page" fallback). */
    action?: { label: string; onClick: () => void }
}): HTMLDivElement => {
    const {
        title,
        description,
        currentStep,
        totalSteps,
        onPrev,
        onNext,
        onClose,
        isLastStep,
        demo,
        loading,
        action,
    } = options

    const demoHTML = getDemoHTML(demo)

    const popover = document.createElement('div')
    popover.className = 'tour-popover-container'
    // #149: `title` is escaped because one caller builds it from API data (the
    // contract tour's config-option names). `description` and `demoHTML` are
    // trusted markup — every caller passes literals or bundled i18n strings,
    // and descriptions carry intended HTML (badges, <br/>). Keep it that way.
    popover.innerHTML = `
        <button class="tour-close" aria-label="${escapeHtml(chrome('tour_popover.close'))}">×</button>
        <h3>${escapeHtml(title)}</h3>
        ${
            loading
                ? '<div class="tour-loading" role="status"><span class="tour-spinner"></span></div>'
                : `<p>${description}</p>`
        }
        ${loading ? '' : demoHTML}
        <div class="tour-footer">
            <div>
                <span class="tour-progress">${currentStep} / ${totalSteps}</span>
                <div class="tour-keyboard-hint">${chrome('tour_popover.keyboard_hint')}</div>
            </div>
            <div class="tour-footer-buttons">
                ${
                    currentStep > 1
                        ? `<button class="tour-btn tour-btn-prev"${loading ? ' disabled' : ''}>${chrome('tour_popover.previous')}</button>`
                        : ''
                }
                ${
                    action
                        ? '<button class="tour-btn tour-btn-action"></button>'
                        : ''
                }
                <button class="tour-btn tour-btn-next"${loading ? ' disabled' : ''}>${
                    isLastStep
                        ? chrome('tour_popover.finish')
                        : chrome('tour_popover.next')
                }</button>
            </div>
        </div>
    `

    // Attach click event listeners
    popover.querySelector('.tour-close')?.addEventListener('click', onClose)
    popover
        .querySelector('.tour-btn-prev')
        ?.addEventListener('click', () => onPrev?.())
    popover.querySelector('.tour-btn-next')?.addEventListener('click', onNext)
    const actionBtn = popover.querySelector('.tour-btn-action')
    if (actionBtn && action) {
        actionBtn.textContent = action.label
        actionBtn.addEventListener('click', action.onClick)
    }

    // Keyboard navigation: Enter/Tab = next, Shift+Tab = prev, Escape = close
    if (currentKeyboardHandler) {
        document.removeEventListener('keydown', currentKeyboardHandler)
    }

    currentKeyboardHandler = (e: KeyboardEvent) => {
        // Ignore if typing in an input field
        const target = e.target as HTMLElement
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            return
        }

        switch (e.key) {
            case 'Enter':
            case 'Tab':
                if (e.shiftKey && e.key === 'Tab') {
                    // Shift+Tab = previous step
                    e.preventDefault()
                    onPrev?.()
                } else {
                    // Enter or Tab = next step
                    e.preventDefault()
                    onNext()
                }
                break
            case 'Escape':
                e.preventDefault()
                onClose()
                break
            case 'ArrowRight':
                e.preventDefault()
                onNext()
                break
            case 'ArrowLeft':
                e.preventDefault()
                onPrev?.()
                break
        }
    }

    document.addEventListener('keydown', currentKeyboardHandler)

    return popover
}

/**
 * Position popover relative to target element
 */
export const positionPopover = (
    popover: HTMLElement,
    targetElement: HTMLElement
): void => {
    const rect = targetElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Layout size, not getBoundingClientRect(): the popover animates in with
    // `scale(0.95)`, so its rect is ~5% short on the first call and a popover
    // anchored to the right edge ended up ~19px outside the viewport.
    const popoverHeight = popover.offsetHeight || 200
    const popoverWidth =
        popover.offsetWidth || Math.min(380, viewportWidth - 20)

    const spaceBelow = viewportHeight - rect.bottom
    const showAbove = spaceBelow < popoverHeight + 20 && rect.top > spaceBelow

    let popoverTop = showAbove
        ? rect.top - popoverHeight - 20
        : rect.bottom + 20
    let popoverLeft = rect.left + rect.width / 2 - popoverWidth / 2

    // On mobile, center horizontally with padding
    if (viewportWidth < 768) {
        popoverLeft = (viewportWidth - popoverWidth) / 2
    }

    popoverLeft = Math.max(
        10,
        Math.min(popoverLeft, viewportWidth - popoverWidth - 10)
    )

    // Ensure popover is within viewport
    if (popoverTop < 10) popoverTop = 10
    if (popoverTop + popoverHeight > viewportHeight - 10) {
        popoverTop = viewportHeight - popoverHeight - 10
    }

    popover.style.top = `${popoverTop}px`
    popover.style.left = `${popoverLeft}px`

    // Follow the target if it moves (see trackTarget)
    trackedPopover = { popover, target: targetElement }
}
